import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';



interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const isDevEnv = import.meta.env.VITE_ENV === 'lovable_or_local';

  const hasLoggedSession = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (_event === 'SIGNED_IN' && session?.user && !hasLoggedSession.current) {
        hasLoggedSession.current = true;
        // Fire-and-forget — do NOT await inside onAuthStateChange to avoid blocking auth propagation
        supabase.from('login_sessions').insert({
          user_id: session.user.id,
          user_agent: navigator.userAgent,
          metadata: {},
        }).then(({ error }) => {
          if (error) console.warn('[auth] Failed to log session:', error.message);
        });
      }
    });

    let removeMessageListener: (() => void) | undefined;

    if (isDevEnv) {
      const handleAuthMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        const payload = event.data as {
          type?: string;
          access_token?: string;
          refresh_token?: string;
        };

        if (
          payload?.type !== 'caldeira-auth-session' ||
          !payload.access_token ||
          !payload.refresh_token
        ) {
          return;
        }

        void supabase.auth.setSession({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token,
        });
      };

      window.addEventListener('message', handleAuthMessage);
      removeMessageListener = () => window.removeEventListener('message', handleAuthMessage);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      removeMessageListener?.();
    };
  }, []);

  const signInWithGoogle = async () => {
    const inIframe = isDevEnv && window.self !== window.top;
    const redirectTo = `${window.location.origin}/auth/callback`;

    if (inIframe) {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: true,
        },
      });

      if (error || !data.url) return;

      window.open(data.url, '_blank');

      // Fallback polling for browsers where storage can still be shared
      const interval = setInterval(async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          clearInterval(interval);
          setSession(sessionData.session);
          setUser(sessionData.session.user);
        }
      }, 1500);

      setTimeout(() => clearInterval(interval), 2 * 60 * 1000);
    } else {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
        },
      });
    }
  };

  const signInWithGitHub = async () => {
    const inIframe = isDevEnv && window.self !== window.top;
    const redirectTo = `${window.location.origin}/auth/callback`;

    if (inIframe) {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: { redirectTo, skipBrowserRedirect: true },
      });
      if (error || !data.url) return;
      window.open(data.url, '_blank');
      const interval = setInterval(async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          clearInterval(interval);
          setSession(sessionData.session);
          setUser(sessionData.session.user);
        }
      }, 1500);
      setTimeout(() => clearInterval(interval), 2 * 60 * 1000);
    } else {
      await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: { redirectTo },
      });
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signInWithGoogle, signInWithGitHub, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
